// app/(owner)/onboarding/basics.tsx

import RequireOwner from "@/components/RequireOwner";
import LocationPinCard from "@/components/owner/LocationPinCard";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import {
  useOnboarding,
  type BusinessType,
  type LocationPinDraft,
} from "@/components/owner/OnboardingProvider";
import StepHeader from "@/components/owner/StepHeader";
import CategorySubcategorySection, {
  type CategorySubcategoryValue,
} from "@/components/taxonomy/CategorySubcategorySection";
import {
  createDefaultBusinessHours,
  formatTimeLabel,
  sanitizeBusinessHours,
} from "@/lib/businessHours";
import { WEB_BASE_DOMAIN } from "@/lib/config/brand";
import { db } from "@/lib/firebase";
import {
  listAreasByCity,
  listCitiesByCounty,
  listCounties,
  type LocationItem,
} from "@/lib/locations";
import {
  BUSINESS_SLUG_UNAVAILABLE_MESSAGE,
  patchBusiness,
  patchBusinessWithSlugReservation,
  persistOwnerOnboardingStep,
  readBusiness,
} from "@/lib/onboardingBusiness";
import {
  resolveOwnerOnboardingAccess,
  selectOwnerLifecycleBusiness,
} from "@/lib/onboardingResume";
import { normalizeLabel } from "@/lib/taxonomy";
import type {
  BusinessHourDayKey,
  BusinessHours,
  BusinessHoursDay,
} from "@/lib/types";
import { slugify, validateBasics } from "@/lib/validation";
import { router, useLocalSearchParams } from "expo-router";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  type KeyboardTypeOptions,
  type LayoutChangeEvent,
  type ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/* =====================================================
   TYPES
===================================================== */

type BusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

type EditMode = "onboarding" | "edit-approved";

type FormState = {
  ownerName: string;
  ownerPhone: string;

  name: string;
  slug: string;

  categoryId: string | null;
  categoryName: string;
  categoryNameNormalized: string;
  categoryStatus: "approved" | "pending";

  subcategoryId: string | null;
  subcategoryName: string;
  subcategoryNameNormalized: string;
  subcategoryStatus: "approved" | "pending";

  category: string;
  subCategoryText: string;

  phone: string;

  businessType: BusinessType | null;

  countyId: string | null;
  countyName: string;

  cityId: string | null;
  cityName: string;

  areaId: string | null;
  areaName: string;

  citySuggestionId: string | null;
  citySuggestionName: string;

  areaSuggestionId: string | null;
  areaSuggestionName: string;

  /**
   * Generated only.
   * Do not expose this as an editable input.
   */
  cityLine: string;

  addressLine: string;

  locationPin: LocationPinDraft | null;
  hasConfirmedLocationPin: boolean;

  businessHours: BusinessHours;
};

type ReadBusinessData = {
  id: string;

  ownerName?: string;
  ownerPhone?: string;

  name?: string;
  slug?: string;

  category?: string;
  subCategory?: string[];
  subCategoryText?: string;

  categoryId?: string | null;
  categoryName?: string;
  categoryNameNormalized?: string;
  categoryStatus?: "approved" | "pending";

  subcategoryId?: string | null;
  subcategoryName?: string;
  subcategoryNameNormalized?: string;
  subcategoryStatus?: "approved" | "pending";

  phone?: string;

  businessType?: BusinessType | null;

  countyId?: string | null;
  countyName?: string;

  cityId?: string | null;
  cityName?: string;
  citySuggestionId?: string | null;
  citySuggestionName?: string | null;

  areaId?: string | null;
  areaName?: string;
  areaSuggestionId?: string | null;
  areaSuggestionName?: string | null;

  cityLine?: string;
  addressLine?: string;

  status?: BusinessStatus;
  isPublished?: boolean;
  isDeleted?: boolean;
  isDeactivated?: boolean;

  location?: {
    countyId?: string | null;
    countyName?: string | null;

    cityId?: string | null;
    cityName?: string | null;
    citySuggestionId?: string | null;
    citySuggestionName?: string | null;

    areaId?: string | null;
    areaName?: string | null;
    areaSuggestionId?: string | null;
    areaSuggestionName?: string | null;
  };

  locationPin?: LocationPinDraft | null;
  hasConfirmedLocationPin?: boolean;

  businessHours?: BusinessHours;
};

type ExistingOwnerBusiness = {
  id: string;
  status?: BusinessStatus;
  isDeleted?: boolean;
  updatedAt?: unknown;
  createdAt?: unknown;
};

type FieldErrors = Partial<
  Record<
    | "ownerName"
    | "ownerPhone"
    | "name"
    | "category"
    | "phone"
    | "businessType"
    | "countyId"
    | "cityId"
    | "areaId"
    | "addressLine"
    | "locationPin",
    string
  >
>;

type BusinessTypeOption = {
  id: NonNullable<BusinessType>;
  name: string;
};

const BASICS_ERROR_ORDER: (keyof FieldErrors)[] = [
  "ownerName",
  "ownerPhone",
  "name",
  "category",
  "businessType",
  "phone",
  "addressLine",
  "locationPin",
  "countyId",
  "cityId",
  "areaId",
];

const FIX_HIGHLIGHTED_FIELDS_MESSAGE =
  "Please fix the highlighted fields before continuing.";

const BUSINESS_TYPE_OPTIONS: BusinessTypeOption[] = [
  { id: "physical_shop", name: "Physical Shop / Office" },
  { id: "freelancer", name: "Freelance" },
  { id: "online_only", name: "Online Shop" },
];

const DAY_OPTIONS: { key: BusinessHourDayKey; label: string }[] = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

/**
 * Owner profile edits keep control fields locked. cityLine is generated, not
 * typed by owners.
 */
const APPROVED_SAFE_FORM_KEYS = new Set<string>([
  "ownerName",
  "ownerPhone",
  "name",

  "categoryId",
  "categoryName",
  "categoryNameNormalized",
  "categoryStatus",

  "subcategoryId",
  "subcategoryName",
  "subcategoryNameNormalized",
  "subcategoryStatus",

  "category",
  "subCategoryText",

  "phone",
  "businessType",

  "countyId",
  "countyName",
  "cityId",
  "cityName",
  "citySuggestionId",
  "citySuggestionName",
  "areaId",
  "areaName",
  "areaSuggestionId",
  "areaSuggestionName",
  "cityLine",

  "addressLine",
  "location",
  "locationPin",
  "hasConfirmedLocationPin",
  "businessHours",
]);

/* =====================================================
   HELPERS
===================================================== */

function useDebouncedPatch(
  fn: (patch: Record<string, unknown>) => void,
  delay = 500
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useCallback(
    (patch: Record<string, unknown>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        fnRef.current(patch);
      }, delay);
    },
    [delay]
  );
}

function getMode(value: unknown): EditMode {
  return value === "edit-approved" ? "edit-approved" : "onboarding";
}

function normalizeStatus(value: unknown): BusinessStatus {
  if (
    value === "draft" ||
    value === "pending" ||
    value === "approved" ||
    value === "rejected" ||
    value === "changes_requested"
  ) {
    return value;
  }

  return "draft";
}

function getBusinessTypeLabel(value: BusinessType | null) {
  return BUSINESS_TYPE_OPTIONS.find((item) => item.id === value)?.name || "";
}

function cleanText(value: unknown) {
  return String(value || "").trim();
}

/**
 * Public location line builder.
 *
 * This is the only source for the public location preview.
 * Owners should never manually type this field.
 */
function buildPublicLocationLine(args: {
  countyName?: string | null;
  cityName?: string | null;
  areaName?: string | null;
}) {
  const county = cleanText(args.countyName);
  const city = cleanText(args.cityName);
  const area = cleanText(args.areaName);

  if (area && city) return `${area}, ${city}`;
  if (city && county) return `${city}, ${county}`;
  if (city) return city;
  if (county) return county;

  return "";
}

function getLocationCityName(form: Pick<FormState, "cityId" | "cityName" | "citySuggestionName">) {
  return form.cityId ? form.cityName : form.citySuggestionName;
}

function getLocationAreaName(form: Pick<FormState, "areaId" | "areaName" | "areaSuggestionName">) {
  return form.areaId ? form.areaName : form.areaSuggestionName;
}

function buildPublicLocationLineFromForm(
  form: Pick<
    FormState,
    | "countyName"
    | "cityId"
    | "cityName"
    | "citySuggestionName"
    | "areaId"
    | "areaName"
    | "areaSuggestionName"
  >
) {
  return buildPublicLocationLine({
    countyName: form.countyName,
    cityName: getLocationCityName(form),
    areaName: getLocationAreaName(form),
  });
}

function hasPendingLocationSuggestion(
  form: Pick<FormState, "citySuggestionId" | "areaSuggestionId">
) {
  return !!form.citySuggestionId || !!form.areaSuggestionId;
}

function toMillis(value: unknown): number {
  if (!value) return 0;

  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (value instanceof Date) return value.getTime();

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  return 0;
}

function getBestExistingBusinessToResume(
  businesses: ExistingOwnerBusiness[]
): ExistingOwnerBusiness | null {
  return selectOwnerLifecycleBusiness(businesses);
}

async function getExistingOwnerBusinesses(
  ownerId: string
): Promise<ExistingOwnerBusiness[]> {
  const q = query(
    collection(db, "businesses"),
    where("ownerId", "==", ownerId),
    limit(50)
  );

  const snap = await getDocs(q);

  return snap.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<ExistingOwnerBusiness, "id">),
    }))
    .filter((business) => business.isDeleted !== true)
    .sort((a, b) => {
      const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
      const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

      return bMs - aMs;
    });
}

async function findReadableSlugConflict(args: {
  slug: string;
  currentBusinessId: string;
  ownerId: string;
}) {
  const cleanSlug = args.slug.trim();

  if (!cleanSlug) return null;

  let ownSnap;

  try {
    ownSnap = await getDocs(
      query(
        collection(db, "businesses"),
        where("ownerId", "==", args.ownerId),
        where("slug", "==", cleanSlug),
        limit(5)
      )
    );
  } catch (error) {
    const code = String((error as { code?: unknown })?.code ?? "")
      .trim()
      .slice(0, 80);
    console.warn("Owner slug conflict query failed.", code ? { code } : undefined);
    throw error;
  }

  const ownConflict = ownSnap.docs.find(
    (docSnap) => docSnap.id !== args.currentBusinessId
  );

  if (ownConflict) return ownConflict.id;

  let publicSnap;

  try {
    publicSnap = await getDocs(
      query(
        collection(db, "businesses"),
        where("slug", "==", cleanSlug),
        where("status", "==", "approved"),
        where("isPublished", "==", true),
        where("isDeleted", "==", false),
        where("isDeactivated", "==", false),
        limit(5)
      )
    );
  } catch (error) {
    const code = String((error as { code?: unknown })?.code ?? "")
      .trim()
      .slice(0, 80);
    console.warn("Public slug conflict query failed.", code ? { code } : undefined);
    throw error;
  }

  return (
    publicSnap.docs.find((docSnap) => docSnap.id !== args.currentBusinessId)
      ?.id ?? null
  );
}

function mapBasicsErrors(v: any): FieldErrors {
  const e: FieldErrors = {};
  const errs = v?.errors || {};

  if (errs.ownerName) e.ownerName = "Owner name is required";
  if (errs.ownerPhone)
    e.ownerPhone = v?.messages?.ownerPhone || "Owner phone is required";
  if (errs.name) e.name = "Business name is required";
  if (errs.category) e.category = "Category is required";
  if (errs.phone)
    e.phone = v?.messages?.phone || "Business phone is required";
  if (errs.businessType) e.businessType = "Business type is required";
  if (errs.countyId) e.countyId = "County is required";
  if (errs.cityId) e.cityId = "City / Town is required";
  if (errs.areaId) e.areaId = "Area is required";
  if (errs.addressLine) e.addressLine = "Shop / Office address is required";
  if (errs.locationPin)
    e.locationPin = "Location Pin is required for physical shop / office";

  return e;
}

function cleanSuggestionLabel(value: string) {
  return String(value || "").trim();
}

async function createLocationSuggestion(args: {
  type: "city" | "area";
  countyId: string;
  countyName: string;
  cityId?: string | null;
  cityName?: string | null;
  citySuggestionId?: string | null;
  citySuggestionName?: string | null;
  label: string;
  createdByUid: string;
}) {
  const label = cleanSuggestionLabel(args.label);

  if (!label) {
    throw new Error("Missing suggestion label");
  }

  const labelLower = normalizeLabel(label);

  if (!labelLower) {
    throw new Error("Invalid suggestion label");
  }

  let ref;

  if (args.type === "city") {
    ref = doc(db, "counties", args.countyId, "citySuggestions", labelLower);
  } else if (args.cityId) {
    ref = doc(
      db,
      "counties",
      args.countyId,
      "cities",
      args.cityId,
      "areaSuggestions",
      labelLower
    );
  } else {
    if (!args.citySuggestionId) {
      throw new Error("Missing citySuggestionId for area suggestion");
    }

    ref = doc(
      db,
      "counties",
      args.countyId,
      "citySuggestions",
      args.citySuggestionId,
      "areaSuggestions",
      labelLower
    );
  }

  const snap = await getDoc(ref);

  if (snap.exists()) {
    const data: any = snap.data();
    const existingLabel = cleanSuggestionLabel(data?.label ?? label);

    if (data?.status === "pending" || data?.status === "approved") {
      return {
        id: ref.id,
        name: existingLabel || label,
        label: existingLabel || label,
        labelLower,
      };
    }

    if (data?.status === "rejected") {
      throw new Error(
        "This location suggestion was previously rejected. Please choose another name or contact support."
      );
    }

    throw new Error(
      "This location suggestion is unavailable. Please choose another name or contact support."
    );
  }

  await setDoc(ref, {
    type: args.type,
    countyId: args.countyId,
    countyName: args.countyName,

    cityId: args.cityId ?? null,
    cityName: args.cityName ?? null,
    citySuggestionId: args.citySuggestionId ?? null,
    citySuggestionName: args.citySuggestionName ?? null,

    label,
    labelLower,
    status: "pending",
    createdByUid: args.createdByUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return {
    id: ref.id,
    name: label,
    label,
    labelLower,
  };
}

function makeSafeApprovedPatch(patch: Record<string, unknown>) {
  const safe: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(patch)) {
    if (APPROVED_SAFE_FORM_KEYS.has(key as keyof FormState)) {
      safe[key] = value;
    }
  }

  return safe;
}

function defaultDay(): BusinessHoursDay {
  return {
    isOpen: true,
    openMinutes: 540,
    closeMinutes: 1020,
  };
}

function normalizeHolidayDateInput(value: string) {
  const clean = String(value || "").trim();
  const match = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(4, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}

/* =====================================================
   SELECT MODAL
===================================================== */

function SelectModal<T extends { id: string; name: string }>(props: {
  label: string;
  placeholder?: string;
  items: T[];
  visible: boolean;
  onClose: () => void;
  onPick: (item: T) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSuggest?: (typed: string) => Promise<void> | void;
  suggestLabel?: (typed: string) => string;
}) {
  const {
    label,
    placeholder,
    items,
    visible,
    onClose,
    onPick,
    searchable = true,
    searchPlaceholder = "Search...",
    onSuggest,
    suggestLabel,
  } = props;

  const [q, setQ] = useState("");

  useEffect(() => {
    if (!visible) setQ("");
  }, [visible]);

  const filtered = useMemo(() => {
    const currentQuery = normalizeLabel(q || "");

    if (!currentQuery) return items;

    return (items || []).filter((item) =>
      normalizeLabel(item.name).includes(currentQuery)
    );
  }, [items, q]);

  const canSuggest = !!onSuggest && !!normalizeLabel(q) && filtered.length === 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={modalBackdrop}>
        <View style={modalCard}>
          <Text style={modalTitle}>{label}</Text>

          {searchable ? (
            <TextInput
              value={q}
              onChangeText={setQ}
              placeholder={searchPlaceholder}
              placeholderTextColor="#9CA3AF"
              autoCapitalize="none"
              style={modalSearchInput}
            />
          ) : null}

          <View style={modalListWrap}>
            <FlatList
              data={filtered}
              keyExtractor={(item) => item.id}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item }) => (
                <TouchableOpacity
                  onPress={() => onPick(item)}
                  style={modalItem}
                >
                  <Text style={modalItemText}>{item.name}</Text>
                </TouchableOpacity>
              )}
              ListEmptyComponent={
                <View style={{ padding: 12 }}>
                  <Text style={{ color: "#666", fontWeight: "700" }}>
                    {placeholder ?? "No options"}
                  </Text>

                  {canSuggest ? (
                    <Pressable
                      onPress={async () => {
                        const typed = String(q || "").trim();
                        if (!typed) return;

                        await onSuggest?.(typed);
                        onClose();
                      }}
                      style={suggestBtn}
                    >
                      <Text style={suggestBtnText}>
                        {suggestLabel ? suggestLabel(q) : `Suggest "${q}"`}
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              }
            />
          </View>

          <Pressable onPress={onClose} style={modalCloseBtn}>
            <Text style={modalCloseText}>Close</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

/* =====================================================
   SCREEN
===================================================== */

export default function Basics() {
  const { user } = useOwnerAuth();

  const {
    businessId,
    setBusinessId,
    setBusinessType,
    locationPinDraft,
    correctionAuthorized,
  } = useOnboarding();

  const insets = useSafeAreaInsets();

  const params = useLocalSearchParams<{
    businessId?: string;
    mode?: string;
  }>();

  const paramBusinessId =
    typeof params.businessId === "string" ? params.businessId : undefined;

  const mode = getMode(params.mode);
  const isApprovedEditMode = mode === "edit-approved";

  const effectiveBusinessId = paramBusinessId || businessId || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [counties, setCounties] = useState<LocationItem[]>([]);
  const [cities, setCities] = useState<LocationItem[]>([]);
  const [areas, setAreas] = useState<LocationItem[]>([]);
  const [locLoading, setLocLoading] = useState(false);

  const [businessTypeModal, setBusinessTypeModal] = useState(false);
  const [countyModal, setCountyModal] = useState(false);
  const [cityModal, setCityModal] = useState(false);
  const [areaModal, setAreaModal] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [form, setForm] = useState<FormState>({
    ownerName: "",
    ownerPhone: "",
    name: "",
    slug: "",

    categoryId: null,
    categoryName: "",
    categoryNameNormalized: "",
    categoryStatus: "approved",

    subcategoryId: null,
    subcategoryName: "",
    subcategoryNameNormalized: "",
    subcategoryStatus: "approved",

    category: "",
    subCategoryText: "",

    phone: "+254",

    businessType: null,

    countyId: null,
    countyName: "",

    cityId: null,
    cityName: "",

    areaId: null,
    areaName: "",

    citySuggestionId: null,
    citySuggestionName: "",

    areaSuggestionId: null,
    areaSuggestionName: "",

    cityLine: "",

    addressLine: "",

    locationPin: null,
    hasConfirmedLocationPin: false,

    businessHours: createDefaultBusinessHours(),
  });

  const businessIdRef = useRef<string | null>(null);
  const creatingDraftRef = useRef(false);
  const scrollRef = useRef<ScrollView | null>(null);
  const fieldPositionsRef = useRef<Partial<Record<keyof FieldErrors, number>>>(
    {}
  );
  const locationGroupYRef = useRef<number | null>(null);
  const locationFieldOffsetsRef = useRef<
    Partial<Record<"countyId" | "cityId" | "areaId", number>>
  >({});
  const fieldInputRefs = useRef<Partial<Record<keyof FieldErrors, TextInput | null>>>(
    {}
  );

  const publicLocationPreview = useMemo(() => {
    return buildPublicLocationLineFromForm(form);
  }, [form]);

  const pendingLocationText = hasPendingLocationSuggestion(form)
    ? "Pending location suggestions are shown here for review. They should be approved by admin before the business goes public."
    : "Generated automatically from selected county, city/town and area.";

  const hasVisibleFieldErrors = useMemo(
    () => Object.values(fieldErrors).some(Boolean),
    [fieldErrors]
  );

  const registerFieldLayout = useCallback(
    (key: keyof FieldErrors) => (event: LayoutChangeEvent) => {
      fieldPositionsRef.current[key] = event.nativeEvent.layout.y;
    },
    []
  );

  const registerLocationGroupLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const groupY = event.nativeEvent.layout.y;
      locationGroupYRef.current = groupY;

      (["countyId", "cityId", "areaId"] as const).forEach((key) => {
        const offset = locationFieldOffsetsRef.current[key];

        if (typeof offset === "number") {
          fieldPositionsRef.current[key] = groupY + offset;
        }
      });
    },
    []
  );

  const registerLocationFieldLayout = useCallback(
    (key: "countyId" | "cityId" | "areaId") =>
      (event: LayoutChangeEvent) => {
        const offset = event.nativeEvent.layout.y;
        locationFieldOffsetsRef.current[key] = offset;

        if (typeof locationGroupYRef.current === "number") {
          fieldPositionsRef.current[key] =
            locationGroupYRef.current + offset;
        }
      },
    []
  );

  const registerFieldInput = useCallback(
    (key: keyof FieldErrors) => (node: TextInput | null) => {
      fieldInputRefs.current[key] = node;
    },
    []
  );

  const focusFirstInvalidField = useCallback((errors: FieldErrors) => {
    const firstKey = BASICS_ERROR_ORDER.find((key) => !!errors[key]);

    if (!firstKey) return;

    const y = fieldPositionsRef.current[firstKey];

    if (typeof y === "number") {
      scrollRef.current?.scrollTo({
        y: Math.max(y - 18, 0),
        animated: true,
      });
    }

    setTimeout(() => {
      fieldInputRefs.current[firstKey]?.focus?.();
    }, 250);
  }, []);

  const showFieldErrors = useCallback(
    (errors: FieldErrors) => {
      setFieldErrors(errors);
      const focus = () => focusFirstInvalidField(errors);

      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(focus);
      } else {
        setTimeout(focus, 0);
      }
    },
    [focusFirstInvalidField]
  );

  useEffect(() => {
    businessIdRef.current = effectiveBusinessId;
  }, [effectiveBusinessId]);

  useEffect(() => {
    if (!user) {
      router.replace("/(owner)/onboarding/signin" as any);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (!paramBusinessId) return;
    if (businessId === paramBusinessId) return;

    setBusinessId(paramBusinessId);
  }, [user, paramBusinessId, businessId, setBusinessId]);

  useEffect(() => {
    if (!user) return;
    if (isApprovedEditMode && !paramBusinessId && !businessId) {
      setLoading(false);
      router.replace("/(owner)/dashboard" as any);
      return;
    }

    if (isApprovedEditMode) return;
    if (paramBusinessId) return;
    if (businessId) return;
    if (creatingDraftRef.current) return;

    creatingDraftRef.current = true;

    (async () => {
      try {
        const existingBusinesses = await getExistingOwnerBusinesses(user.uid);
        const businessToResume =
          getBestExistingBusinessToResume(existingBusinesses);

        if (businessToResume?.id) {
          setBusinessId(businessToResume.id);
          return;
        }

        if (existingBusinesses.length > 0) {
          setLoading(false);
          router.replace("/(owner)/dashboard" as any);
          return;
        }

        setLoading(false);
        router.replace("/(owner)/start" as any);
      } catch (error: any) {
        console.warn("Business onboarding preparation failed.");

        setLoading(false);

        Alert.alert(
          "Could not open onboarding",
          error?.message ||
            "We could not prepare your business onboarding. Please try again.",
          [
            {
              text: "OK",
              onPress: () => router.replace("/(owner)" as any),
            },
          ]
        );
      } finally {
        creatingDraftRef.current = false;
      }
    })();
  }, [
    user,
    businessId,
    setBusinessId,
    paramBusinessId,
    isApprovedEditMode,
  ]);

  const patchBusinessNow = useCallback(
    (patch: Record<string, unknown>) => {
      const id = businessIdRef.current;
      if (!id) return;

      const finalPatch = isApprovedEditMode
        ? makeSafeApprovedPatch(patch)
        : patch;

      if (Object.keys(finalPatch).length === 0) {
        return;
      }

      Promise.resolve(patchBusiness(id, finalPatch)).catch(() => {
        console.warn("Business onboarding auto-save failed.");
      });
    },
    [isApprovedEditMode]
  );

  const doPatchBusiness = useDebouncedPatch(patchBusinessNow, 500);

  const clearError = useCallback((key: keyof FieldErrors) => {
    setFieldErrors((previous) => {
      if (!previous[key]) return previous;

      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const setField = (key: keyof FormState, value: string | boolean | null) => {
    if (submitError) setSubmitError(null);

    const isSensitive =
      isApprovedEditMode && !APPROVED_SAFE_FORM_KEYS.has(key);

    if (isSensitive) {
      Alert.alert(
        "Admin review needed",
        "This approved business field is locked for now. It should be changed through an admin-reviewed edit request later."
      );
      return;
    }

    if (key === "slug") {
      return;
    }

    const nextForm = { ...form, [key]: value } as FormState;

    if (key === "name" && !isApprovedEditMode) {
      nextForm.slug = slugify(String(value || ""));
    }

    setForm((previous) => {
      const next = { ...previous, [key]: value } as FormState;

      if (key === "name" && !isApprovedEditMode) {
        next.slug = slugify(String(value || ""));
      }

      return next;
    });

    if (key === "ownerName" && String(value || "").trim()) {
      clearError("ownerName");
    } else if (key === "ownerPhone") {
      const nextErrors = mapBasicsErrors(validateBasics(nextForm));
      if (!nextErrors.ownerPhone) clearError("ownerPhone");
    } else if (key === "name" && slugify(String(value || ""))) {
      clearError("name");
    } else if (key === "phone") {
      const nextErrors = mapBasicsErrors(validateBasics(nextForm));
      if (!nextErrors.phone) clearError("phone");
    } else if (
      key === "addressLine" &&
      (nextForm.businessType !== "physical_shop" ||
        String(value || "").trim())
    ) {
      clearError("addressLine");
    }

    const patch: Record<string, unknown> =
      key === "name" ? { name: value, businessName: value } : { [key]: value };

    if (key === "subCategoryText") {
      patch.subCategory = String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      delete patch.subCategoryText;
    }

    doPatchBusiness(patch);
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLocLoading(true);
        const data = await listCounties(db);

        if (!alive) return;

        setCounties(Array.isArray(data) ? data : []);
      } catch {
        console.warn("County loading failed.");

        if (alive) {
          Alert.alert("Location", "Failed to load counties.");
          setCounties([]);
        }
      } finally {
        if (alive) setLocLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!form.countyId) {
        setCities([]);
        setAreas([]);
        return;
      }

      try {
        setLocLoading(true);
        const data = await listCitiesByCounty(db, form.countyId);

        if (!alive) return;

        setCities(Array.isArray(data) ? data : []);
        setAreas([]);
      } catch {
        console.warn("City loading failed.");

        if (alive) {
          Alert.alert("Location", "Failed to load cities.");
          setCities([]);
          setAreas([]);
        }
      } finally {
        if (alive) setLocLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [form.countyId]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!form.countyId || !form.cityId) {
        setAreas([]);
        return;
      }

      try {
        setLocLoading(true);
        const data = await listAreasByCity(db, form.countyId, form.cityId);

        if (!alive) return;

        setAreas(Array.isArray(data) ? data : []);
      } catch {
        console.warn("Area loading failed.");

        if (alive) {
          Alert.alert("Location", "Failed to load areas.");
          setAreas([]);
        }
      } finally {
        if (alive) setLocLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [form.countyId, form.cityId]);

  useEffect(() => {
    if (!user || !effectiveBusinessId) return;

    let cancelled = false;

    (async () => {
      try {
        const data = (await readBusiness(
          effectiveBusinessId
        )) as ReadBusinessData | null;

        if (cancelled) return;

        if (!data) {
          setLoading(false);
          return;
        }

        const onboardingAccess = resolveOwnerOnboardingAccess({
          status: data.status,
          isPublished: data.isPublished,
          isDeleted: data.isDeleted,
          approvedEditMode: isApprovedEditMode,
          correctionAuthorized,
        });

        if (onboardingAccess === "review") {
          setLoading(false);
          router.replace({
            pathname: "/(owner)/onboarding/review",
            params: { businessId: effectiveBusinessId },
          } as any);
          return;
        }

        if (onboardingAccess === "dashboard") {
          setLoading(false);
          router.replace("/(owner)/dashboard" as any);
          return;
        }

        if (isApprovedEditMode) {
          const status = normalizeStatus(data.status);

          if (status !== "approved") {
            Alert.alert(
              "Not approved",
              "This business is not approved yet. Opening normal onboarding instead."
            );
          }
        }

        const legacyCategory = data.category || "";

        const legacySubText = Array.isArray(data.subCategory)
          ? data.subCategory.join(", ")
          : data.subCategoryText || "";

        const catName = data.categoryName || legacyCategory || "";
        const subName = data.subcategoryName || legacySubText || "";

        const loc = data.location || {};

        const countyId = (loc.countyId ?? data.countyId ?? null) as
          | string
          | null;

        const cityId = (loc.cityId ?? data.cityId ?? null) as string | null;
        const areaId = (loc.areaId ?? data.areaId ?? null) as string | null;

        const countyName = String(loc.countyName ?? data.countyName ?? "");
        const cityName = String(loc.cityName ?? data.cityName ?? "");
        const areaName = String(loc.areaName ?? data.areaName ?? "");

        const citySuggestionId = (
          loc.citySuggestionId ??
          data.citySuggestionId ??
          null
        ) as string | null;

        const citySuggestionName = String(
          loc.citySuggestionName ?? data.citySuggestionName ?? ""
        );

        const areaSuggestionId = (
          loc.areaSuggestionId ??
          data.areaSuggestionId ??
          null
        ) as string | null;

        const areaSuggestionName = String(
          loc.areaSuggestionName ?? data.areaSuggestionName ?? ""
        );

        const cityLine = buildPublicLocationLine({
          countyName,
          cityName: cityId ? cityName : citySuggestionName || cityName,
          areaName: areaId ? areaName : areaSuggestionName || areaName,
        });

        const nextBusinessType = (data.businessType ?? null) as
          | BusinessType
          | null;

        if (nextBusinessType) {
          setBusinessType(nextBusinessType);
        }

        setForm((previous) => ({
          ...previous,

          ownerName: data.ownerName || "",
          ownerPhone: data.ownerPhone || "",

          name: data.name || "",
          slug: slugify(data.name || ""),

          categoryId: (data.categoryId ?? null) as string | null,
          categoryName: catName,
          categoryNameNormalized:
            data.categoryNameNormalized || normalizeLabel(catName),
          categoryStatus: (data.categoryStatus as any) || "approved",

          subcategoryId: (data.subcategoryId ?? null) as string | null,
          subcategoryName: subName,
          subcategoryNameNormalized:
            data.subcategoryNameNormalized || normalizeLabel(subName),
          subcategoryStatus: (data.subcategoryStatus as any) || "approved",

          category: catName,
          subCategoryText: subName,

          phone: data.phone || "+254",

          businessType: nextBusinessType,

          countyId,
          countyName,

          cityId,
          cityName,

          areaId,
          areaName,

          citySuggestionId,
          citySuggestionName,

          areaSuggestionId,
          areaSuggestionName,

          cityLine,

          addressLine: data.addressLine || "",

          locationPin: (data.locationPin ?? null) as LocationPinDraft | null,
          hasConfirmedLocationPin: !!data.hasConfirmedLocationPin,

          businessHours: data.businessHours
            ? sanitizeBusinessHours(data.businessHours)
            : createDefaultBusinessHours(),
        }));
      } catch (error: any) {
        console.warn("Business onboarding load failed.");
        Alert.alert("Error", error?.message || "Could not load business");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    user,
    effectiveBusinessId,
    setBusinessType,
    isApprovedEditMode,
    correctionAuthorized,
  ]);

  useEffect(() => {
    if (!locationPinDraft) return;

    setForm((previous) => ({
      ...previous,
      locationPin: locationPinDraft,
      hasConfirmedLocationPin: !!locationPinDraft.confirmedByOwner,
    }));

    clearError("locationPin");

    doPatchBusiness({
      locationPin: locationPinDraft,
      hasConfirmedLocationPin: !!locationPinDraft.confirmedByOwner,
    });
  }, [locationPinDraft, clearError, doPatchBusiness]);

  const generatedSlug = useMemo(() => slugify(form.name), [form.name]);
  const publicSlug = isApprovedEditMode
    ? form.slug || generatedSlug
    : generatedSlug;

  const publicLink = publicSlug
    ? `${WEB_BASE_DOMAIN}/p/${publicSlug}`
    : `${WEB_BASE_DOMAIN}/p/your-business`;

  const applyLocation = (next: {
    county?: LocationItem | null;
    city?: LocationItem | null;
    area?: LocationItem | null;
  }) => {
    const countyId =
      next.county === null ? null : next.county?.id ?? form.countyId;

    const countyName =
      next.county === null ? "" : next.county?.name ?? form.countyName;

    const countyChanged =
      next.county !== undefined &&
      next.county !== null &&
      next.county.id !== form.countyId;

    const cityId =
      next.city === null
        ? null
        : countyChanged
        ? null
        : next.city?.id ?? form.cityId;

    const cityName =
      next.city === null
        ? ""
        : countyChanged
        ? ""
        : next.city?.name ?? form.cityName;

    const cityChanged =
      next.city !== undefined &&
      next.city !== null &&
      next.city.id !== form.cityId;

    const areaId =
      next.area === null
        ? null
        : countyChanged || cityChanged
        ? null
        : next.area?.id ?? form.areaId;

    const areaName =
      next.area === null
        ? ""
        : countyChanged || cityChanged
        ? ""
        : next.area?.name ?? form.areaName;

    const citySuggestionId =
      countyChanged || next.city ? null : form.citySuggestionId;

    const citySuggestionName =
      countyChanged || next.city ? "" : form.citySuggestionName;

    const areaSuggestionId =
      countyChanged || cityChanged || next.area ? null : form.areaSuggestionId;

    const areaSuggestionName =
      countyChanged || cityChanged || next.area ? "" : form.areaSuggestionName;

    const cityLine = buildPublicLocationLine({
      countyName,
      cityName: cityId ? cityName : citySuggestionName,
      areaName: areaId ? areaName : areaSuggestionName,
    });

    setForm((previous) => ({
      ...previous,

      countyId,
      countyName,

      cityId,
      cityName,

      areaId,
      areaName,

      citySuggestionId,
      citySuggestionName,

      areaSuggestionId,
      areaSuggestionName,

      cityLine,
    }));

    if (countyId) clearError("countyId");
    if (cityId || citySuggestionId) clearError("cityId");
    if (areaId || areaSuggestionId) clearError("areaId");

    doPatchBusiness({
      location: {
        countyId: countyId ?? null,
        countyName: countyName || null,

        cityId: cityId ?? null,
        cityName: cityName || null,
        citySuggestionId: citySuggestionId ?? null,
        citySuggestionName: citySuggestionName || null,

        areaId: areaId ?? null,
        areaName: areaName || null,
        areaSuggestionId: areaSuggestionId ?? null,
        areaSuggestionName: areaSuggestionName || null,
      },

      countyId: countyId ?? null,
      countyName: countyName || null,

      cityId: cityId ?? null,
      cityName: cityName || null,
      citySuggestionId: citySuggestionId ?? null,
      citySuggestionName: citySuggestionName || null,

      areaId: areaId ?? null,
      areaName: areaName || null,
      areaSuggestionId: areaSuggestionId ?? null,
      areaSuggestionName: areaSuggestionName || null,

      cityLine,
    });
  };

  const chooseBusinessType = (nextType: NonNullable<BusinessType>) => {
    setBusinessType(nextType);
    clearError("businessType");

    setForm((previous) => {
      if (nextType === "physical_shop") {
        return {
          ...previous,
          businessType: nextType,
        };
      }

      return {
        ...previous,
        businessType: nextType,
        addressLine: "",
        locationPin: null,
        hasConfirmedLocationPin: false,
      };
    });

    const patch: Record<string, unknown> = {
      businessType: nextType,
    };

    if (nextType !== "physical_shop") {
      patch.addressLine = "";
      patch.locationPin = null;
      patch.hasConfirmedLocationPin = false;
      clearError("addressLine");
      clearError("locationPin");
    }

    doPatchBusiness(patch);
  };

  const updateHours = (mutate: (previous: BusinessHours) => BusinessHours) => {
    setForm((previous) => {
      const nextHours = sanitizeBusinessHours(mutate(previous.businessHours));

      doPatchBusiness({
        businessHours: nextHours,
      });

      return {
        ...previous,
        businessHours: nextHours,
      };
    });
  };

  const saveBasics = async () => {
    const id = businessIdRef.current;

    if (saving) return;

    setSubmitError(null);

    if (!user || !id) {
      const message = "Please sign in again and try.";
      setSubmitError(message);
      Alert.alert("Missing info", message);
      return;
    }

    const generatedCityLine = buildPublicLocationLineFromForm(form);
    const generatedSlugForSave = slugify(form.name);

    if (!isApprovedEditMode) {
      const validation: any = validateBasics(form);

      if (!validation.ok) {
        const inline = mapBasicsErrors(validation);
        setSubmitError(FIX_HIGHLIGHTED_FIELDS_MESSAGE);
        showFieldErrors(inline);
        return;
      }

      const hasCity = !!form.cityId || !!form.citySuggestionId;
      const hasArea = !!form.areaId || !!form.areaSuggestionId;

      if (!form.countyId || !hasCity || !hasArea) {
        setSubmitError(FIX_HIGHLIGHTED_FIELDS_MESSAGE);
        showFieldErrors({
          ...fieldErrors,
          countyId: !form.countyId
            ? "County is required"
            : fieldErrors.countyId,
          cityId: !hasCity
            ? "City / Town is required"
            : fieldErrors.cityId,
          areaId: !hasArea ? "Area is required" : fieldErrors.areaId,
        });
        return;
      }

      if (
        form.businessType === "physical_shop" &&
        !form.hasConfirmedLocationPin
      ) {
        setSubmitError(FIX_HIGHLIGHTED_FIELDS_MESSAGE);
        showFieldErrors({
          ...fieldErrors,
          locationPin: "Location Pin is required for physical shop / office",
        });
        return;
      }

      if (!generatedSlugForSave) {
        setSubmitError(FIX_HIGHLIGHTED_FIELDS_MESSAGE);
        showFieldErrors({
          ...fieldErrors,
          name: "Use letters or numbers in the Business Name",
        });
        return;
      }

      if (hasVisibleFieldErrors) {
        setFieldErrors({});
      }
    }

    setSaving(true);

    try {
      if (!isApprovedEditMode) {
        const conflictId = await findReadableSlugConflict({
          slug: generatedSlugForSave,
          currentBusinessId: id,
          ownerId: user.uid,
        });

        if (conflictId) {
          setSubmitError(BUSINESS_SLUG_UNAVAILABLE_MESSAGE);
          showFieldErrors({
            ...fieldErrors,
            name: "Make the Business Name more specific",
          });

          Alert.alert(
            "Business name needs more detail",
            BUSINESS_SLUG_UNAVAILABLE_MESSAGE
          );
          return;
        }
      }

      const commonSafePayload: Record<string, unknown> = {
        ownerName: String(form.ownerName || "").trim(),
        ownerPhone: String(form.ownerPhone || "").trim(),
        phone: String(form.phone || "").trim(),
        addressLine: String(form.addressLine || "").trim(),
        businessHours: sanitizeBusinessHours(form.businessHours),
      };

      if (form.businessType === "physical_shop") {
        commonSafePayload.locationPin = form.locationPin
          ? {
              lat: form.locationPin.lat,
              lng: form.locationPin.lng,
              accuracyMeters: form.locationPin.accuracyMeters ?? null,
              source: "current_location",
              confirmedByOwner: !!form.locationPin.confirmedByOwner,
            }
          : null;

        commonSafePayload.hasConfirmedLocationPin =
          !!form.hasConfirmedLocationPin;
      } else {
        commonSafePayload.locationPin = null;
        commonSafePayload.hasConfirmedLocationPin = false;
        commonSafePayload.addressLine = "";
      }

      const payload: Record<string, unknown> = {
        ...commonSafePayload,

        name: String(form.name || "").trim(),
        businessName: String(form.name || "").trim(),

        categoryId: form.categoryId ?? null,
        categoryName: form.categoryName,
        categoryNameNormalized: form.categoryNameNormalized,
        categoryStatus: form.categoryStatus,

        subcategoryId: form.subcategoryId ?? null,
        subcategoryName: form.subcategoryName,
        subcategoryNameNormalized: form.subcategoryNameNormalized,
        subcategoryStatus: form.subcategoryStatus,

        category: form.categoryName,
        subCategory: (form.subcategoryName || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),

        businessType: form.businessType ?? null,

        location: {
          countyId: form.countyId ?? null,
          countyName: form.countyName || null,

          cityId: form.cityId ?? null,
          cityName: form.cityName || null,
          citySuggestionId: form.citySuggestionId ?? null,
          citySuggestionName: form.citySuggestionName || null,

          areaId: form.areaId ?? null,
          areaName: form.areaName || null,
          areaSuggestionId: form.areaSuggestionId ?? null,
          areaSuggestionName: form.areaSuggestionName || null,
        },

        countyId: form.countyId ?? null,
        countyName: form.countyName || null,

        cityId: form.cityId ?? null,
        cityName: form.cityName || null,
        citySuggestionId: form.citySuggestionId ?? null,
        citySuggestionName: form.citySuggestionName || null,

        areaId: form.areaId ?? null,
        areaName: form.areaName || null,
        areaSuggestionId: form.areaSuggestionId ?? null,
        areaSuggestionName: form.areaSuggestionName || null,

        /**
         * Generated, not manually typed.
         */
        cityLine: generatedCityLine,
      };

      if (isApprovedEditMode) {
        await patchBusiness(id, payload);

        router.push({
          pathname: "/(owner)/onboarding/brand",
          params: {
            businessId: id,
            mode: "edit-approved",
          },
        } as any);

        return;
      }

      payload.slug = generatedSlugForSave;

      await patchBusinessWithSlugReservation(
        id,
        payload,
        generatedSlugForSave
      );
      await persistOwnerOnboardingStep("brand");

      router.push({
        pathname: "/(owner)/onboarding/brand",
        params: {
          businessId: id,
          mode: "onboarding",
        },
      } as any);
    } catch (error: any) {
      console.warn("Business basics save failed.");
      const message = error?.message || "Unknown error";
      const title =
        message === BUSINESS_SLUG_UNAVAILABLE_MESSAGE
          ? "Business name needs more detail"
          : "Could not continue";
      setSubmitError(message);

      Alert.alert(title, message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireOwner>
      {loading ? (
        <View style={loadingWrap}>
          <ActivityIndicator />

          <Text style={{ marginTop: 12, fontWeight: "900" }}>Loading...</Text>
        </View>
      ) : (
        <View style={screen}>
          <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior={
              Platform.OS === "ios"
                ? "padding"
                : Platform.OS === "android"
                ? "height"
                : undefined
            }
            keyboardVerticalOffset={Platform.OS === "android" ? 24 : 0}
          >
            <ScrollView
              ref={scrollRef}
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: 16,
                paddingBottom: 32,
                flexGrow: 1,
              }}
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
            >
              <View style={container}>
                <View style={card}>
                  <StepHeader
                    title={
                      isApprovedEditMode
                        ? "Edit Business Profile"
                        : "Step 1: Basics"
                    }
                    subtitle={
                      isApprovedEditMode
                        ? "Update the details customers see on your profile."
                        : "Required — owner phone, business name, category, business type, contact, location and hours."
                    }
                    showBack={false as any}
                  />

                  {hasVisibleFieldErrors ? (
                    <View style={topErrorBox}>
                      <Text style={topErrorText}>
                        Please fix the highlighted fields before continuing.
                      </Text>
                    </View>
                  ) : null}

                  <SectionTitle
                    title="Owner profile"
                    subtitle="Used for account recovery and optional phone sign-in."
                  />

                  <Field
                    label="Owner Name *"
                    placeholder="e.g. John Mwangi"
                    value={form.ownerName}
                    onChange={(value) => setField("ownerName", value)}
                    error={fieldErrors.ownerName}
                    inputRef={registerFieldInput("ownerName")}
                    onLayout={registerFieldLayout("ownerName")}
                  />

                  <Field
                    label="Owner Phone *"
                    placeholder="e.g. +254 712 345 678"
                    helper="Required — used for account reset and optional mobile sign-in."
                    keyboardType="phone-pad"
                    value={form.ownerPhone}
                    onChange={(value) => setField("ownerPhone", value)}
                    error={fieldErrors.ownerPhone}
                    inputRef={registerFieldInput("ownerPhone")}
                    onLayout={registerFieldLayout("ownerPhone")}
                  />

                  <Divider />

                  <SectionTitle
                    title="Business basics"
                    subtitle={
                      isApprovedEditMode
                        ? "Customers will see these updates on your Soko Grid card."
                        : "This is what customers will see on your Soko Grid card."
                    }
                  />

                  <Field
                    label="Business Name *"
                    placeholder="e.g. Premier Electric"
                    value={form.name}
                    onChange={(value) => setField("name", value)}
                    error={fieldErrors.name}
                    inputRef={registerFieldInput("name")}
                    onLayout={registerFieldLayout("name")}
                  />

                  <View style={{ marginTop: 12 }}>
                    <Text style={labelStyle}>Public profile link</Text>

                    <View style={readOnlyBox}>
                      <Text style={readOnlyText} numberOfLines={1}>
                        {publicLink}
                      </Text>
                    </View>

                    <Text style={helperStyle}>
                      Generated automatically from your Business Name.
                    </Text>
                  </View>

                  <View
                    style={{ marginTop: 12 }}
                    onLayout={registerFieldLayout("category")}
                  >
                    <Text style={labelStyle}>Category *</Text>

                    <View
                      style={[
                        fieldErrors.category ? categoryErrorWrap : null,
                      ]}
                    >
                      <CategorySubcategorySection
                        value={{
                          categoryId: form.categoryId,
                          categoryName: form.categoryName,
                          categoryNameNormalized: form.categoryNameNormalized,
                          categoryStatus: form.categoryStatus,

                          subcategoryId: form.subcategoryId,
                          subcategoryName: form.subcategoryName,
                          subcategoryNameNormalized:
                            form.subcategoryNameNormalized,
                          subcategoryStatus: form.subcategoryStatus,
                        }}
                        onChange={(next: CategorySubcategoryValue) => {
                          setForm((previous) => ({
                            ...previous,

                            categoryId: next.categoryId,
                            categoryName: next.categoryName,
                            categoryNameNormalized:
                              next.categoryNameNormalized,
                            categoryStatus: next.categoryStatus,

                            subcategoryId: next.subcategoryId,
                            subcategoryName: next.subcategoryName,
                            subcategoryNameNormalized:
                              next.subcategoryNameNormalized,
                            subcategoryStatus: next.subcategoryStatus,

                            category: next.categoryName,
                            subCategoryText: next.subcategoryName || "",
                          }));

                          clearError("category");

                          doPatchBusiness({
                            categoryId: next.categoryId,
                            categoryName: next.categoryName,
                            categoryNameNormalized:
                              next.categoryNameNormalized,
                            categoryStatus: next.categoryStatus,

                            subcategoryId: next.subcategoryId,
                            subcategoryName: next.subcategoryName,
                            subcategoryNameNormalized:
                              next.subcategoryNameNormalized,
                            subcategoryStatus: next.subcategoryStatus,

                            category: next.categoryName,
                            subCategoryText: next.subcategoryName,
                            subCategory: (next.subcategoryName || "")
                              .split(",")
                              .map((item) => item.trim())
                              .filter(Boolean),
                          });
                        }}
                      />
                    </View>

                    {!!fieldErrors.category ? (
                      <Text style={errorStyle}>{fieldErrors.category}</Text>
                    ) : null}

                    <Text style={helperStyle}>
                      Pick the closest match. If it’s missing, add it for
                      approval.
                    </Text>
                  </View>

                  <View
                    style={{ marginTop: 14 }}
                    onLayout={registerFieldLayout("businessType")}
                  >
                    <Text style={labelStyle}>Business Type *</Text>

                    <Pressable
                      onPress={() => {
                        setBusinessTypeModal(true);
                      }}
                      style={[
                        selectorBtnLast,
                        { marginTop: 8 },
                        fieldErrors.businessType ? selectorBtnError : null,
                      ]}
                    >
                      <Text
                        style={{
                          color: form.businessType ? "#111827" : "#6B7280",
                          fontWeight: "700",
                        }}
                      >
                        {form.businessType
                          ? getBusinessTypeLabel(form.businessType)
                          : "Select Business Type"}
                      </Text>
                    </Pressable>

                    {!!fieldErrors.businessType ? (
                      <Text style={errorStyle}>
                        {fieldErrors.businessType}
                      </Text>
                    ) : null}

                    <SelectModal<BusinessTypeOption>
                      label="Select Business Type"
                      visible={businessTypeModal}
                      onClose={() => setBusinessTypeModal(false)}
                      items={BUSINESS_TYPE_OPTIONS}
                      placeholder="No business types found"
                      searchable={false}
                      onPick={(picked) => {
                        setBusinessTypeModal(false);
                        chooseBusinessType(picked.id);
                      }}
                    />
                  </View>

                  {form.businessType ? (
                    <>
                      <Divider />

                      <SectionTitle
                        title="Contact & location"
                        subtitle="Customers use this to call you or find your business."
                      />

                      <Field
                        label="Business Phone *"
                        placeholder="+254..."
                        keyboardType="phone-pad"
                        value={form.phone}
                        onChange={(value) => setField("phone", value)}
                        error={fieldErrors.phone}
                        inputRef={registerFieldInput("phone")}
                        onLayout={registerFieldLayout("phone")}
                      />

                      {form.businessType === "physical_shop" ? (
                        <>
                          <Field
                            label="Shop / Office Address *"
                            placeholder="e.g. D12, 1st Floor, DK House"
                            helper="Exact building, office, shop number, street, landmark, or floor."
                            value={form.addressLine}
                            onChange={(value) =>
                              setField("addressLine", value)
                            }
                            error={fieldErrors.addressLine}
                            inputRef={registerFieldInput("addressLine")}
                            onLayout={registerFieldLayout("addressLine")}
                          />

                          <View
                            style={{ marginTop: 14 }}
                            onLayout={registerFieldLayout("locationPin")}
                          >
                            <View
                              style={
                                fieldErrors.locationPin
                                  ? locationPinErrorWrap
                                  : null
                              }
                            >
                              <LocationPinCard
                                saved={!!form.hasConfirmedLocationPin}
                                onPress={() => {
                                  if (!businessIdRef.current) {
                                    Alert.alert(
                                      "Location Pin",
                                      "Business draft is still being prepared. Try again."
                                    );
                                    return;
                                  }

                                  try {
                                    router.push({
                                      pathname: "/onboarding/location-pin",
                                      params: {
                                        businessId: businessIdRef.current,
                                        mode,
                                      },
                                    } as any);
                                  } catch (error: any) {
                                    console.warn("Location pin could not be opened.");
                                    Alert.alert(
                                      "Location Pin",
                                      error?.message ||
                                        "Could not open Location Pin. Please try again."
                                    );
                                  }
                                }}
                              />
                            </View>

                            {!!fieldErrors.locationPin ? (
                              <Text style={errorStyle}>
                                {fieldErrors.locationPin}
                              </Text>
                            ) : null}
                          </View>
                        </>
                      ) : null}

                      <View
                        style={{ marginTop: 14 }}
                        onLayout={registerLocationGroupLayout}
                      >
                        <Text style={labelStyle}>
                          {form.businessType === "physical_shop"
                            ? "Location *"
                            : "Main Area Served *"}
                        </Text>

                        <Pressable
                          onLayout={registerLocationFieldLayout("countyId")}
                          onPress={() => {
                            setCountyModal(true);
                          }}
                          style={[
                            selectorBtn,
                            fieldErrors.countyId ? selectorBtnError : null,
                          ]}
                        >
                          <Text
                            style={{
                              color: form.countyId ? "#111827" : "#6B7280",
                              fontWeight: "700",
                            }}
                          >
                            {form.countyId ? form.countyName : "Select County"}
                          </Text>
                        </Pressable>

                        {!!fieldErrors.countyId ? (
                          <Text style={errorStyle}>{fieldErrors.countyId}</Text>
                        ) : null}

                        <Pressable
                          onLayout={registerLocationFieldLayout("cityId")}
                          onPress={() => {
                            if (!form.countyId) {
                              Alert.alert("Location", "Select a county first.");
                              return;
                            }

                            setCityModal(true);
                          }}
                          style={[
                            selectorBtn,
                            fieldErrors.cityId ? selectorBtnError : null,
                          ]}
                        >
                          <Text
                            style={{
                              color:
                                form.cityId || form.citySuggestionId
                                  ? "#111827"
                                  : "#6B7280",
                              fontWeight: "700",
                            }}
                          >
                            {form.cityId
                              ? form.cityName
                              : form.citySuggestionId
                              ? `${form.citySuggestionName} (Pending)`
                              : "Select City/Town"}
                          </Text>
                        </Pressable>

                        {!!fieldErrors.cityId ? (
                          <Text style={errorStyle}>{fieldErrors.cityId}</Text>
                        ) : null}

                        <Pressable
                          onLayout={registerLocationFieldLayout("areaId")}
                          onPress={() => {
                            if (!form.countyId) {
                              Alert.alert("Location", "Select a county first.");
                              return;
                            }

                            if (!form.cityId && !form.citySuggestionId) {
                              Alert.alert(
                                "Location",
                                "Select or suggest a city first."
                              );
                              return;
                            }

                            setAreaModal(true);
                          }}
                          style={[
                            selectorBtnLast,
                            fieldErrors.areaId ? selectorBtnError : null,
                          ]}
                        >
                          <Text
                            style={{
                              color:
                                form.areaId || form.areaSuggestionId
                                  ? "#111827"
                                  : "#6B7280",
                              fontWeight: "700",
                            }}
                          >
                            {form.areaId
                              ? form.areaName
                              : form.areaSuggestionId
                              ? `${form.areaSuggestionName} (Pending)`
                              : "Select Area/Neighborhood"}
                          </Text>
                        </Pressable>

                        {!!fieldErrors.areaId ? (
                          <Text style={errorStyle}>{fieldErrors.areaId}</Text>
                        ) : null}

                        <ReadOnlyPreview
                          label="Public location preview"
                          value={
                            publicLocationPreview ||
                            "Select county, city/town and area"
                          }
                          helper={pendingLocationText}
                          pending={hasPendingLocationSuggestion(form)}
                        />

                        {locLoading ? (
                          <View style={loadingInline}>
                            <ActivityIndicator />

                            <Text style={loadingInlineText}>
                              Loading locations.
                            </Text>
                          </View>
                        ) : null}
                      </View>

                      <Divider />

                      <BusinessHoursEditor
                        value={form.businessHours}
                        onChange={updateHours}
                      />
                    </>
                  ) : null}
                </View>
              </View>
            </ScrollView>

            <View
              style={[
                footer,
                {
                  paddingBottom: Math.max(insets.bottom, 16),
                },
              ]}
            >
              <View style={footerInner}>
                {submitError ? (
                  <Text style={footerErrorText}>{submitError}</Text>
                ) : null}

                <Pressable
                  onPress={saveBasics}
                  disabled={saving}
                  style={[
                    btnPrimary,
                    saving ? { opacity: 0.45 } : null,
                  ]}
                >
                  <Text style={btnPrimaryText}>
                    {saving
                      ? "Saving..."
                      : isApprovedEditMode
                      ? "Save & Next: Brand Links →"
                      : "Next: Brand →"}
                  </Text>
                </Pressable>
              </View>
            </View>
          </KeyboardAvoidingView>

          <SelectModal<LocationItem>
            label="Select County"
            visible={countyModal}
            onClose={() => setCountyModal(false)}
            items={counties}
            placeholder="No counties found"
            searchPlaceholder="Search county."
            onPick={(picked) => {
              setCountyModal(false);
              setCities([]);
              setAreas([]);
              applyLocation({
                county: picked,
                city: null,
                area: null,
              });
            }}
          />

          <SelectModal<LocationItem>
            label="Select City/Town"
            visible={cityModal}
            onClose={() => setCityModal(false)}
            items={cities}
            placeholder="No cities found"
            searchPlaceholder="Search city/town."
            onPick={(picked) => {
              setCityModal(false);
              setAreas([]);
              applyLocation({
                city: picked,
                area: null,
              });
            }}
            onSuggest={
              user && form.countyId
                ? async (typed) => {
                    const suggestion = await createLocationSuggestion({
                      type: "city",
                      countyId: form.countyId as string,
                      countyName: form.countyName,
                      label: typed,
                      createdByUid: user.uid,
                    });

                    const cityLine = buildPublicLocationLine({
                      countyName: form.countyName,
                      cityName: suggestion.name,
                      areaName: "",
                    });

                    setForm((previous) => ({
                      ...previous,
                      cityId: null,
                      cityName: "",
                      citySuggestionId: suggestion.id,
                      citySuggestionName: suggestion.name,

                      areaId: null,
                      areaName: "",
                      areaSuggestionId: null,
                      areaSuggestionName: "",

                      cityLine,
                    }));

                    clearError("cityId");

                    doPatchBusiness({
                      location: {
                        countyId: form.countyId,
                        countyName: form.countyName || null,

                        cityId: null,
                        cityName: null,
                        citySuggestionId: suggestion.id,
                        citySuggestionName: suggestion.name,

                        areaId: null,
                        areaName: null,
                        areaSuggestionId: null,
                        areaSuggestionName: null,
                      },

                      cityId: null,
                      cityName: null,
                      citySuggestionId: suggestion.id,
                      citySuggestionName: suggestion.name,

                      areaId: null,
                      areaName: null,
                      areaSuggestionId: null,
                      areaSuggestionName: null,

                      cityLine,
                    });
                  }
                : undefined
            }
            suggestLabel={(typed) => `Suggest "${typed}"`}
          />

          <SelectModal<LocationItem>
            label="Select Area/Neighborhood"
            visible={areaModal}
            onClose={() => setAreaModal(false)}
            items={areas}
            placeholder="No areas found"
            searchPlaceholder="Search area/neighborhood."
            onPick={(picked) => {
              setAreaModal(false);
              applyLocation({
                area: picked,
              });
            }}
            onSuggest={
              user &&
              form.countyId &&
              (form.cityId || form.citySuggestionId)
                ? async (typed) => {
                    const suggestion = await createLocationSuggestion({
                      type: "area",
                      countyId: form.countyId as string,
                      countyName: form.countyName,
                      cityId: form.cityId,
                      cityName: form.cityName,
                      citySuggestionId: form.citySuggestionId,
                      citySuggestionName: form.citySuggestionName,
                      label: typed,
                      createdByUid: user.uid,
                    });

                    const cityLine = buildPublicLocationLine({
                      countyName: form.countyName,
                      cityName: form.cityId
                        ? form.cityName
                        : form.citySuggestionName,
                      areaName: suggestion.name,
                    });

                    setForm((previous) => ({
                      ...previous,
                      areaId: null,
                      areaName: "",
                      areaSuggestionId: suggestion.id,
                      areaSuggestionName: suggestion.name,
                      cityLine,
                    }));

                    clearError("areaId");

                    doPatchBusiness({
                      location: {
                        countyId: form.countyId,
                        countyName: form.countyName || null,

                        cityId: form.cityId ?? null,
                        cityName: form.cityName || null,
                        citySuggestionId: form.citySuggestionId ?? null,
                        citySuggestionName: form.citySuggestionName || null,

                        areaId: null,
                        areaName: null,
                        areaSuggestionId: suggestion.id,
                        areaSuggestionName: suggestion.name,
                      },

                      citySuggestionId: form.citySuggestionId ?? null,
                      citySuggestionName: form.citySuggestionName || null,

                      areaId: null,
                      areaName: null,
                      areaSuggestionId: suggestion.id,
                      areaSuggestionName: suggestion.name,

                      cityLine,
                    });
                  }
                : undefined
            }
            suggestLabel={(typed) => `Suggest "${typed}"`}
          />
        </View>
      )}
    </RequireOwner>
  );
}

/* =====================================================
   SMALL COMPONENTS
===================================================== */

function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <View style={{ marginTop: 6, marginBottom: 10 }}>
      <Text style={sectionTitle}>{title}</Text>
      {!!subtitle ? <Text style={sectionSubtitle}>{subtitle}</Text> : null}
    </View>
  );
}

function Divider() {
  return <View style={divider} />;
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  helper,
  hint,
  error,
  keyboardType,
  autoCapitalize,
  editable = true,
  inputRef,
  onLayout,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helper?: string;
  hint?: string;
  error?: string;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  editable?: boolean;
  inputRef?: (node: TextInput | null) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
}) {
  return (
    <View style={{ marginTop: 10 }} onLayout={onLayout}>
      <Text style={labelStyle}>{label}</Text>

      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor="#9CA3AF"
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        editable={editable}
        style={[
          input,
          error ? inputError : null,
          !editable ? disabledBox : null,
        ]}
      />

      {!!helper ? <Text style={helperStyle}>{helper}</Text> : null}
      {!!hint ? <Text style={hintStyle}>{hint}</Text> : null}
      {!!error ? <Text style={errorStyle}>{error}</Text> : null}
    </View>
  );
}

function ReadOnlyPreview({
  label,
  value,
  helper,
  pending,
}: {
  label: string;
  value: string;
  helper?: string;
  pending?: boolean;
}) {
  return (
    <View style={{ marginTop: 14 }}>
      <Text style={labelStyle}>{label}</Text>

      <View style={[readOnlyBox, pending ? readOnlyBoxPending : null]}>
        <Text style={readOnlyText}>{value}</Text>
      </View>

      {!!helper ? (
        <Text style={pending ? pendingHelperStyle : helperStyle}>{helper}</Text>
      ) : null}
    </View>
  );
}

type TimeSelectorTarget =
  | BusinessHourDayKey
  | "publicHoliday"
  | `holidayDate:${string}`;

function holidayDateTimeTarget(dateKey: string): TimeSelectorTarget {
  return `holidayDate:${dateKey}`;
}

function getHolidayDateFromTimeTarget(target: TimeSelectorTarget | null) {
  if (!target?.startsWith("holidayDate:")) return null;

  return target.slice("holidayDate:".length);
}

function isBusinessHourDayTarget(
  target: TimeSelectorTarget
): target is BusinessHourDayKey {
  return DAY_OPTIONS.some((day) => day.key === target);
}

function BusinessHoursEditor({
  value,
  onChange,
}: {
  value: BusinessHours;
  onChange: (mutate: (previous: BusinessHours) => BusinessHours) => void;
}) {
  const hours = sanitizeBusinessHours(value);
  const [holidayDateDraft, setHolidayDateDraft] = useState("");
  const [timeModalVisible, setTimeModalVisible] = useState(false);
  const [selectedTimeTarget, setSelectedTimeTarget] =
    useState<TimeSelectorTarget | null>(null);
  const [selectedField, setSelectedField] = useState<
    "openMinutes" | "closeMinutes" | null
  >(null);

  const timeOptions = useMemo(
    () => Array.from({ length: 48 }, (_, index) => index * 30),
    []
  );

  const visibleDays = hours.sameHoursEveryDay
    ? [{ key: "monday" as BusinessHourDayKey, label: "Every day" }]
    : DAY_OPTIONS;

  const copyDayToAllDays = (
    weekly: BusinessHours["weekly"],
    nextDay: BusinessHoursDay
  ) => {
    const nextWeekly = { ...weekly };

    for (const day of DAY_OPTIONS) {
      nextWeekly[day.key] = { ...nextDay };
    }

    return nextWeekly;
  };

  const closeTimeSelector = () => {
    setTimeModalVisible(false);
    setSelectedTimeTarget(null);
    setSelectedField(null);
  };

  const openTimeSelector = (
    target: TimeSelectorTarget,
    field: "openMinutes" | "closeMinutes"
  ) => {
    setSelectedTimeTarget(target);
    setSelectedField(field);
    setTimeModalVisible(true);
  };

  const updateDay = (
    key: BusinessHourDayKey,
    mutate: (day: BusinessHoursDay) => BusinessHoursDay
  ) => {
    onChange((previous) => {
      const clean = sanitizeBusinessHours(previous);
      const sourceKey = clean.sameHoursEveryDay ? "monday" : key;
      const sourceDay = clean.weekly[sourceKey] || defaultDay();
      const nextDay = mutate(sourceDay);

      return {
        ...clean,
        weekly: clean.sameHoursEveryDay
          ? copyDayToAllDays(clean.weekly, nextDay)
          : {
              ...clean.weekly,
              [key]: nextDay,
            },
      };
    });
  };

  const updateHolidayDay = (
    mutate: (day: BusinessHoursDay) => BusinessHoursDay
  ) => {
    onChange((previous) => {
      const clean = sanitizeBusinessHours(previous);
      const nextDay = mutate(clean.publicHolidays.customDay || defaultDay());

      return {
        ...clean,
        publicHolidays: {
          ...clean.publicHolidays,
          mode: "custom",
          customDay: nextDay,
        },
      };
    });
  };

  const updateHolidayDateDay = (
    dateKey: string,
    mutate: (day: BusinessHoursDay) => BusinessHoursDay
  ) => {
    onChange((previous) => {
      const clean = sanitizeBusinessHours(previous);
      const currentDay = clean.holidayDates[dateKey] || defaultDay();

      return {
        ...clean,
        holidayDates: {
          ...clean.holidayDates,
          [dateKey]: mutate(currentDay),
        },
      };
    });
  };

  const addHolidayDate = () => {
    const dateKey = normalizeHolidayDateInput(holidayDateDraft);

    if (!dateKey) {
      Alert.alert("Invalid date", "Use YYYY-MM-DD.");
      return;
    }

    onChange((previous) => {
      const clean = sanitizeBusinessHours(previous);

      return {
        ...clean,
        holidayDates: {
          ...clean.holidayDates,
          [dateKey]:
            clean.holidayDates[dateKey] ||
            clean.publicHolidays.customDay ||
            defaultDay(),
        },
      };
    });

    setHolidayDateDraft("");
  };

  const removeHolidayDate = (dateKey: string) => {
    onChange((previous) => {
      const clean = sanitizeBusinessHours(previous);
      const nextHolidayDates = { ...clean.holidayDates };

      delete nextHolidayDates[dateKey];

      return {
        ...clean,
        holidayDates: nextHolidayDates,
      };
    });
  };

  const selectTime = (minutes: number) => {
    if (!selectedTimeTarget || !selectedField) return;

    const field = selectedField;
    const holidayDateKey = getHolidayDateFromTimeTarget(selectedTimeTarget);

    if (holidayDateKey) {
      updateHolidayDateDay(holidayDateKey, (previous) => ({
        ...previous,
        [field]: minutes,
      }));
    } else if (selectedTimeTarget === "publicHoliday") {
      updateHolidayDay((previous) => ({
        ...previous,
        [field]: minutes,
      }));
    } else if (isBusinessHourDayTarget(selectedTimeTarget)) {
      updateDay(selectedTimeTarget, (previous) => ({
        ...previous,
        [field]: minutes,
      }));
    }

    closeTimeSelector();
  };

  const selectedHolidayDateKey =
    getHolidayDateFromTimeTarget(selectedTimeTarget);
  const selectedCurrentMinutes =
    !selectedTimeTarget || !selectedField
      ? null
      : selectedHolidayDateKey
      ? hours.holidayDates[selectedHolidayDateKey]?.[selectedField]
      : selectedTimeTarget === "publicHoliday"
      ? hours.publicHolidays.customDay?.[selectedField]
      : isBusinessHourDayTarget(selectedTimeTarget)
      ? hours.weekly[selectedTimeTarget]?.[selectedField]
      : null;
  const holidayDay = hours.publicHolidays.customDay || defaultDay();
  const holidayDateEntries = Object.entries(hours.holidayDates || {}).sort(
    ([a], [b]) => a.localeCompare(b)
  );

  return (
    <View>
      <SectionTitle
        title="Opening hours"
        subtitle="Show customers when your business is open."
      />

      <ToggleRow
        title="Open 24 hours"
        subtitle="Show this business as always open unless temporarily closed."
        active={hours.is24Hours}
        onPress={() =>
          onChange((previous) => ({
            ...sanitizeBusinessHours(previous),
            is24Hours: !sanitizeBusinessHours(previous).is24Hours,
            temporarilyClosed: false,
          }))
        }
      />

      <ToggleRow
        title="Temporarily closed"
        subtitle="Use this when the business should show as closed for now."
        active={hours.temporarilyClosed}
        danger
        onPress={() =>
          onChange((previous) => ({
            ...sanitizeBusinessHours(previous),
            temporarilyClosed: !sanitizeBusinessHours(previous)
              .temporarilyClosed,
          }))
        }
      />

      <ToggleRow
        title="Same hours every day"
        subtitle="Turn off if each day has different opening hours."
        active={hours.sameHoursEveryDay}
        onPress={() =>
          onChange((previous) => {
            const clean = sanitizeBusinessHours(previous);
            const nextSameHoursEveryDay = !clean.sameHoursEveryDay;

            if (!nextSameHoursEveryDay) {
              return {
                ...clean,
                sameHoursEveryDay: false,
              };
            }

            return {
              ...clean,
              sameHoursEveryDay: true,
              weekly: copyDayToAllDays(
                clean.weekly,
                clean.weekly.monday || defaultDay()
              ),
            };
          })
        }
      />

      {!hours.is24Hours && !hours.temporarilyClosed ? (
        <View style={{ marginTop: 10, gap: 8 }}>
          {visibleDays.map((day) => {
            const row = hours.weekly[day.key] || defaultDay();

            return (
              <View key={day.key} style={dayRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={dayLabel}>{day.label}</Text>

                  <Text style={daySubLabel}>
                    {row.isOpen
                      ? `${formatTimeLabel(
                          row.openMinutes ?? 540
                        )} - ${formatTimeLabel(row.closeMinutes ?? 1020)}`
                      : "Closed"}
                  </Text>
                </View>

                <Pressable
                  onPress={() =>
                    updateDay(day.key, (previous) => ({
                      ...previous,
                      isOpen: !previous.isOpen,
                    }))
                  }
                  style={[miniBtn, row.isOpen ? miniBtnDark : null]}
                >
                  <Text
                    style={[
                      miniBtnText,
                      row.isOpen ? miniBtnTextDark : null,
                    ]}
                  >
                    {row.isOpen ? "Open" : "Closed"}
                  </Text>
                </Pressable>

                {row.isOpen ? (
                  <>
                    <Pressable
                      onPress={() =>
                        openTimeSelector(day.key, "openMinutes")
                      }
                      style={timeBtn}
                    >
                      <Text style={timeBtnText}>
                        {formatTimeLabel(row.openMinutes ?? 540)}
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={() =>
                        openTimeSelector(day.key, "closeMinutes")
                      }
                      style={timeBtn}
                    >
                      <Text style={timeBtnText}>
                        {formatTimeLabel(row.closeMinutes ?? 1020)}
                      </Text>
                    </Pressable>
                  </>
                ) : null}
              </View>
            );
          })}
        </View>
      ) : null}

      <Modal
        visible={timeModalVisible}
        transparent
        animationType="fade"
        onRequestClose={closeTimeSelector}
      >
        <View style={modalBackdrop}>
          <View style={modalCard}>
            <Text style={modalTitle}>
              {selectedField === "closeMinutes"
                ? "Select closing time"
                : "Select opening time"}
            </Text>

            <View style={modalListWrap}>
              <FlatList
                data={timeOptions}
                keyExtractor={(item) => String(item)}
                showsVerticalScrollIndicator
                renderItem={({ item }) => {
                  const selected = item === selectedCurrentMinutes;

                  return (
                    <Pressable
                      onPress={() => selectTime(item)}
                      style={[
                        modalItem,
                        selected ? timeModalItemActive : null,
                      ]}
                    >
                      <Text
                        style={[
                          modalItemText,
                          selected ? timeModalItemTextActive : null,
                        ]}
                      >
                        {formatTimeLabel(item)}
                      </Text>
                    </Pressable>
                  );
                }}
              />
            </View>

            <Pressable onPress={closeTimeSelector} style={modalCloseBtn}>
              <Text style={modalCloseText}>Close</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <View style={holidayBox}>
        <Text style={holidayTitle}>Public holidays</Text>

        <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
          <Pressable
            onPress={() =>
              onChange((previous) => {
                const clean = sanitizeBusinessHours(previous);

                return {
                  ...clean,
                  publicHolidays: {
                    ...clean.publicHolidays,
                    mode: "closed",
                  },
                };
              })
            }
            style={[
              holidayBtn,
              hours.publicHolidays.mode === "closed" ? holidayBtnActive : null,
            ]}
          >
            <Text
              style={[
                holidayBtnText,
                hours.publicHolidays.mode === "closed"
                  ? holidayBtnTextActive
                  : null,
              ]}
            >
              Closed
            </Text>
          </Pressable>

          <Pressable
            onPress={() =>
              onChange((previous) => {
                const clean = sanitizeBusinessHours(previous);

                return {
                  ...clean,
                  publicHolidays: {
                    ...clean.publicHolidays,
                    mode: "custom",
                  },
                };
              })
            }
            style={[
              holidayBtn,
              hours.publicHolidays.mode === "custom" ? holidayBtnActive : null,
            ]}
          >
            <Text
              style={[
                holidayBtnText,
                hours.publicHolidays.mode === "custom"
                  ? holidayBtnTextActive
                  : null,
              ]}
            >
              Custom hours
            </Text>
          </Pressable>
        </View>

        {hours.publicHolidays.mode === "custom" ? (
          <View style={[dayRow, { marginTop: 10, flexWrap: "wrap" }]}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={dayLabel}>Holiday hours</Text>

              <Text style={daySubLabel}>
                {holidayDay.isOpen
                  ? `${formatTimeLabel(
                      holidayDay.openMinutes ?? 540
                    )} - ${formatTimeLabel(holidayDay.closeMinutes ?? 1020)}`
                  : "Closed"}
              </Text>
            </View>

            <Pressable
              onPress={() =>
                updateHolidayDay((previous) => ({
                  ...previous,
                  isOpen: !previous.isOpen,
                }))
              }
              style={[miniBtn, holidayDay.isOpen ? miniBtnDark : null]}
            >
              <Text
                style={[
                  miniBtnText,
                  holidayDay.isOpen ? miniBtnTextDark : null,
                ]}
              >
                {holidayDay.isOpen ? "Open" : "Closed"}
              </Text>
            </Pressable>

            {holidayDay.isOpen ? (
              <>
                <Pressable
                  onPress={() =>
                    openTimeSelector("publicHoliday", "openMinutes")
                  }
                  style={timeBtn}
                >
                  <Text style={timeBtnText}>
                    {formatTimeLabel(holidayDay.openMinutes ?? 540)}
                  </Text>
                </Pressable>

                <Pressable
                  onPress={() =>
                    openTimeSelector("publicHoliday", "closeMinutes")
                  }
                  style={timeBtn}
                >
                  <Text style={timeBtnText}>
                    {formatTimeLabel(holidayDay.closeMinutes ?? 1020)}
                  </Text>
                </Pressable>
              </>
            ) : null}
          </View>
        ) : null}

        <View style={{ marginTop: 14 }}>
          <Text style={holidayTitle}>Specific dates</Text>

          <View style={{ flexDirection: "row", gap: 8 }}>
            <TextInput
              value={holidayDateDraft}
              onChangeText={setHolidayDateDraft}
              placeholder="YYYY-MM-DD"
              placeholderTextColor="#9CA3AF"
              autoCapitalize="none"
              style={[input, { flex: 1, minWidth: 0 }]}
            />

            <Pressable onPress={addHolidayDate} style={holidayBtn}>
              <Text style={holidayBtnText}>Add</Text>
            </Pressable>
          </View>

          {holidayDateEntries.map(([dateKey, specialDay]) => (
            <View
              key={dateKey}
              style={[dayRow, { marginTop: 8, flexWrap: "wrap" }]}
            >
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={dayLabel}>{dateKey}</Text>

                <Text style={daySubLabel}>
                  {specialDay.isOpen
                    ? `${formatTimeLabel(
                        specialDay.openMinutes ?? 540
                      )} - ${formatTimeLabel(specialDay.closeMinutes ?? 1020)}`
                    : "Closed"}
                </Text>
              </View>

              <Pressable
                onPress={() =>
                  updateHolidayDateDay(dateKey, (previous) => ({
                    ...previous,
                    isOpen: !previous.isOpen,
                  }))
                }
                style={[miniBtn, specialDay.isOpen ? miniBtnDark : null]}
              >
                <Text
                  style={[
                    miniBtnText,
                    specialDay.isOpen ? miniBtnTextDark : null,
                  ]}
                >
                  {specialDay.isOpen ? "Open" : "Closed"}
                </Text>
              </Pressable>

              {specialDay.isOpen ? (
                <>
                  <Pressable
                    onPress={() =>
                      openTimeSelector(
                        holidayDateTimeTarget(dateKey),
                        "openMinutes"
                      )
                    }
                    style={timeBtn}
                  >
                    <Text style={timeBtnText}>
                      {formatTimeLabel(specialDay.openMinutes ?? 540)}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={() =>
                      openTimeSelector(
                        holidayDateTimeTarget(dateKey),
                        "closeMinutes"
                      )
                    }
                    style={timeBtn}
                  >
                    <Text style={timeBtnText}>
                      {formatTimeLabel(specialDay.closeMinutes ?? 1020)}
                    </Text>
                  </Pressable>
                </>
              ) : null}

              <Pressable
                onPress={() => removeHolidayDate(dateKey)}
                style={miniBtn}
              >
                <Text style={miniBtnText}>Remove</Text>
              </Pressable>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

function ToggleRow({
  title,
  subtitle,
  active,
  danger,
  onPress,
}: {
  title: string;
  subtitle?: string;
  active: boolean;
  danger?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable onPress={onPress} style={toggleRow}>
      <View style={{ flex: 1 }}>
        <Text style={toggleTitle}>{title}</Text>
        {!!subtitle ? <Text style={toggleSubtitle}>{subtitle}</Text> : null}
      </View>

      <View
        style={[
          togglePill,
          active
            ? {
                backgroundColor: danger ? "#DC2626" : "#111827",
                borderColor: danger ? "#DC2626" : "#111827",
              }
            : null,
        ]}
      >
        <Text style={[togglePillText, active ? { color: "#FFFFFF" } : null]}>
          {active ? "On" : "Off"}
        </Text>
      </View>
    </Pressable>
  );
}

/* =====================================================
   STYLES
===================================================== */

const screen = {
  flex: 1,
  backgroundColor: "#F3F4F6",
};

const loadingWrap = {
  flex: 1,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#F3F4F6",
};

const container: ViewStyle = {
  width: "100%",
  maxWidth: 760,
  alignSelf: "center",
};

const card = {
  backgroundColor: "#FFFFFF",
  borderRadius: 20,
  padding: 16,
  borderWidth: 1,
  borderColor: "#E5E7EB",
};

const sectionTitle = {
  fontSize: 18,
  fontWeight: "900" as const,
  color: "#111827",
};

const sectionSubtitle = {
  marginTop: 4,
  fontSize: 12,
  lineHeight: 18,
  color: "#6B7280",
  fontWeight: "700" as const,
};

const divider = {
  height: 1,
  backgroundColor: "#E5E7EB",
  marginVertical: 18,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: "900" as const,
  color: "#374151",
  marginBottom: 6,
};

const topErrorBox = {
  marginTop: 12,
  marginBottom: 4,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#FCA5A5",
  backgroundColor: "#FEF2F2",
  padding: 12,
};

const topErrorText = {
  color: "#991B1B",
  fontSize: 12,
  lineHeight: 18,
  fontWeight: "900" as const,
};

const footerErrorText = {
  color: "#B91C1C",
  fontSize: 13,
  lineHeight: 18,
  fontWeight: "800" as const,
  marginBottom: 10,
  textAlign: "center" as const,
};

const input = {
  minHeight: 46,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  paddingHorizontal: 12,
  backgroundColor: "#FFFFFF",
  color: "#111827",
  fontWeight: "800" as const,
};

const inputError = {
  borderColor: "#EF4444",
};

const categoryErrorWrap = {
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#EF4444",
  padding: 8,
};

const locationPinErrorWrap = {
  borderRadius: 18,
  borderWidth: 1,
  borderColor: "#EF4444",
  padding: 4,
};

const disabledBox = {
  opacity: 0.65,
  backgroundColor: "#F3F4F6",
};

const readOnlyBox = {
  minHeight: 46,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  paddingHorizontal: 12,
  justifyContent: "center" as const,
  backgroundColor: "#F9FAFB",
};

const readOnlyBoxPending = {
  borderColor: "#F59E0B",
  backgroundColor: "#FFFBEB",
};

const readOnlyText = {
  color: "#111827",
  fontWeight: "900" as const,
};

const helperStyle = {
  marginTop: 6,
  fontSize: 11,
  lineHeight: 17,
  fontWeight: "800" as const,
  color: "#6B7280",
};

const pendingHelperStyle = {
  marginTop: 6,
  fontSize: 11,
  lineHeight: 17,
  fontWeight: "900" as const,
  color: "#92400E",
};

const hintStyle = {
  marginTop: 4,
  fontSize: 11,
  lineHeight: 17,
  fontWeight: "900" as const,
  color: "#111827",
};

const errorStyle = {
  marginTop: 6,
  fontSize: 11,
  fontWeight: "900" as const,
  color: "#EF4444",
};

const selectorBtn = {
  minHeight: 46,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  borderRadius: 14,
  paddingHorizontal: 12,
  justifyContent: "center" as const,
  backgroundColor: "#FFFFFF",
  marginTop: 8,
};

const selectorBtnLast = {
  ...selectorBtn,
};

const selectorBtnError = {
  borderColor: "#EF4444",
};

const loadingInline = {
  marginTop: 10,
  flexDirection: "row" as const,
  alignItems: "center" as const,
};

const loadingInlineText = {
  marginLeft: 8,
  color: "#6B7280",
  fontWeight: "700" as const,
};

const footer = {
  paddingHorizontal: 16,
  paddingTop: 12,
  backgroundColor: "#FFFFFF",
  borderTopWidth: StyleSheet.hairlineWidth,
  borderTopColor: "#E5E7EB",
};

const footerInner: ViewStyle = {
  width: "100%",
  maxWidth: 760,
  alignSelf: "center",
};

const btnPrimary = {
  height: 46,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const btnPrimaryText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
};

/* modal */

const modalBackdrop = {
  flex: 1,
  backgroundColor: "rgba(0,0,0,0.4)",
  padding: 18,
  justifyContent: "center" as const,
};

const modalCard = {
  backgroundColor: "#FFFFFF",
  borderRadius: 16,
  padding: 14,
  maxHeight: "80%" as const,
};

const modalTitle = {
  fontSize: 16,
  fontWeight: "900" as const,
  marginBottom: 10,
  color: "#111827",
};

const modalSearchInput = {
  height: 44,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  paddingHorizontal: 12,
  color: "#111827",
  fontWeight: "800" as const,
};

const modalListWrap = {
  maxHeight: 320,
  marginTop: 10,
};

const modalItem = {
  minHeight: 46,
  justifyContent: "center" as const,
  borderBottomWidth: 1,
  borderBottomColor: "#F3F4F6",
};

const modalItemText = {
  color: "#111827",
  fontWeight: "800" as const,
};

const modalCloseBtn = {
  marginTop: 12,
  height: 44,
  borderRadius: 12,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const modalCloseText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
};

const suggestBtn = {
  marginTop: 12,
  minHeight: 42,
  borderRadius: 12,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 12,
};

const suggestBtnText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
};

/* hours */

const toggleRow = {
  minHeight: 64,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  borderRadius: 16,
  padding: 12,
  marginTop: 8,
  flexDirection: "row" as const,
  alignItems: "center" as const,
  gap: 10,
  backgroundColor: "#FFFFFF",
};

const toggleTitle = {
  fontSize: 13,
  color: "#111827",
  fontWeight: "900" as const,
};

const toggleSubtitle = {
  marginTop: 3,
  fontSize: 11,
  lineHeight: 16,
  color: "#6B7280",
  fontWeight: "700" as const,
};

const togglePill = {
  minWidth: 48,
  height: 28,
  borderRadius: 999,
  borderWidth: 1,
  borderColor: "#D1D5DB",
  backgroundColor: "#FFFFFF",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 8,
};

const togglePillText = {
  color: "#6B7280",
  fontWeight: "900" as const,
  fontSize: 11,
};

const dayRow = {
  borderWidth: 1,
  borderColor: "#E5E7EB",
  borderRadius: 16,
  padding: 10,
  flexDirection: "row" as const,
  alignItems: "center" as const,
  gap: 8,
  backgroundColor: "#FFFFFF",
};

const dayLabel = {
  color: "#111827",
  fontWeight: "900" as const,
  fontSize: 12,
};

const daySubLabel = {
  marginTop: 2,
  color: "#6B7280",
  fontWeight: "700" as const,
  fontSize: 11,
};

const miniBtn = {
  height: 34,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: "#D1D5DB",
  paddingHorizontal: 10,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#FFFFFF",
};

const miniBtnDark = {
  backgroundColor: "#111827",
  borderColor: "#111827",
};

const miniBtnText = {
  color: "#374151",
  fontWeight: "900" as const,
  fontSize: 11,
};

const miniBtnTextDark = {
  color: "#FFFFFF",
};

const timeBtn = {
  height: 34,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  paddingHorizontal: 8,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#F9FAFB",
};

const timeBtnText = {
  color: "#111827",
  fontWeight: "900" as const,
  fontSize: 11,
};

const timeModalItemActive = {
  backgroundColor: "#F3F4F6",
};

const timeModalItemTextActive = {
  color: "#111827",
  fontWeight: "900" as const,
};

const holidayBox = {
  marginTop: 14,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  padding: 12,
  backgroundColor: "#F9FAFB",
};

const holidayTitle = {
  color: "#111827",
  fontWeight: "900" as const,
  marginBottom: 10,
};

const holidayBtn = {
  height: 36,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: "#D1D5DB",
  paddingHorizontal: 12,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#FFFFFF",
};

const holidayBtnActive = {
  backgroundColor: "#111827",
  borderColor: "#111827",
};

const holidayBtnText = {
  color: "#374151",
  fontWeight: "900" as const,
  fontSize: 12,
};

const holidayBtnTextActive = {
  color: "#FFFFFF",
};
